
import { defineComponent, onMounted, reactive, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatDate, formatThousands, toStringMaximumTimeOfDay } from '@/utils'
import { createModel } from '@/utils/component'
import { getBOCReceiptPageList } from '@/apis/accepted-bill/acceptanceReceipt'
import ApprovalProcessDialog from '@/views/acceptance/acceptance-receipt/ApprovalProcessDialog.vue'
import AcceptanceReceiptDetail from '@/views/acceptance/acceptance-receipt/AcceptanceReceiptDetail.vue'
import BankOfChinaApprovedDialog from '@/views/acceptance/acceptance-receipt/tabs/BankOfChinaApprovedDialog.vue'
import { ElMessage } from 'element-plus'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTableForIcon from '@/components/table/simple/SimpleTableForIcon.vue'
import {
  ReceiveAcceptanceHistoryModel,
  AcceptanceReceiptPageBOCParamModel
} from '@/model/accepted-bill/acceptedBillModel'
import { formatDraftType } from '@/utils/acceptanceBillFormatUtil'

export default defineComponent({
  name: 'BankOfChinaTab',
  components: {
    AcceptanceReceiptDetail,
    ApprovalProcessDialog,
    BankOfChinaApprovedDialog,
    SearchForm,
    SimpleTableForIcon
  },
  props: {
    isActive: {
      type: Boolean,
      required: true
    },
    bankCode: {
      type: String,
      required: true
    },
    bankName: {
      type: String,
      required: true
    },
    companyCode: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n()
    const tableRef = ref()
    const formatIconTooltip = (tip: string) => {
      if (tip.includes('-')) {
        const tipSplit = tip.split('-')
        const tipCode = tipSplit[0]
        const tipMessage = tipSplit.filter((t, i) => i > 0).join('')
        return `${tipCode}：${tipMessage}`
      }
      return tip
    }
    const statusOptions = [
      { label: t('acceptanceStatus.pendingConfirmation'), value: 0 },
      { label: t('acceptanceStatus.underReview'), value: 1 },
      { label: t('acceptanceStatus.pendingAction'), value: 2 },
      { label: t('acceptanceStatus.operated'), value: 3 },
      { label: t('acceptanceStatus.received'), value: 98 },
      { label: t('acceptanceStatus.rejected'), value: 99 }
    ]
    const searchStruct = [
      {
        prop: 'dateScopeRange',
        label: t('Acceptance.dateScopeRange'),
        component: 'el-date-picker',
        type: 'daterange',
        model: []
      },
      {
        prop: 'dueDateScopeRange',
        label: t('Acceptance.dueDateScopeRange'),
        component: 'el-date-picker',
        type: 'daterange',
        model: []
      },
      { prop: 'draftNo', label: t('Acceptance.draftNo'), component: 'el-input', type: 'text', model: '' }
    ]
    const acceptanceReceiptTableStruct = [
      { prop: 'index', label: t('Acceptance.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'requestObject', label: t('Acceptance.requestObject') },
      { prop: 'draftNo', label: t('Acceptance.draftNo'), width: 260 },
      {
        prop: 'requestDate',
        label: t('Acceptance.requestDate'),
        formatter: (row: ReceiveAcceptanceHistoryModel) => formatDate(row.requestDate, 'yyyy-MM-dd')
      },
      { prop: 'draftType', label: t('Acceptance.draftType'), width: 180, formatter: (row: ReceiveAcceptanceHistoryModel) => formatDraftType(row.draftType) },
      { prop: 'bankName', label: t('Acceptance.bankName'), width: 180 },
      { prop: 'draftAmt', label: t('Acceptance.draftAmt'), formatter: (row: any) => formatThousands(row.draftAmt), align: 'right' },
      {
        prop: 'status',
        label: t('Acceptance.status'),
        formatter: (row: ReceiveAcceptanceHistoryModel) => {
          return statusOptions.find(item => item.value === row.status)?.label
        },
        icon: (row: ReceiveAcceptanceHistoryModel) => {
          /**
           * status 支付单工作表状态，0：待确认，1: 审批中, 2: 待操作, 3: 已操作, 98: 签收, 99: 拒绝
           * receive_type 承兑接收类型，0：待确认，1: 签收, 2: 拒绝
           * submit_status 发起承兑接收/拒绝请求后状态，0：未完成，1：成功，2：失败
           * */
          if (row.status === 2 && row.receiveType !== 0 && row.submitStatus === 2) {
            return 'warning'
          } else {
            return ''
          }
        },
        showIcon: (row: any) => (row.status === 2 && row.receiveType !== 0 && row.submitStatus === 2),
        iconTooltip: 'submitComment',
        formatterIconTooltip: (tip: string) => formatIconTooltip(tip)
      }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as any,
      tableData: Array<ReceiveAcceptanceHistoryModel>(),
      columnStruct: acceptanceReceiptTableStruct,
      selectedRows: Array<ReceiveAcceptanceHistoryModel>(),
      approvalProcessDialogVisible: false,
      detailDialogVisible: false,
      approvedDialogVisible: false,
      approvalData: null as ReceiveAcceptanceHistoryModel | null,
      receiveType: null as number | null,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      selectable(row: ReceiveAcceptanceHistoryModel) {
        return row.status === 0
      },
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }

        dataMap.listLoading = true
        let dateScopeFrom = null as string | null
        let dateScopeTo = null as string | null
        if (dataMap.searchFormModel.dateScopeRange && dataMap.searchFormModel.dateScopeRange.length > 1) {
          dateScopeFrom = dataMap.searchFormModel.dateScopeRange[0] ? formatDate(dataMap.searchFormModel.dateScopeRange[0]) : null
          dateScopeTo = dataMap.searchFormModel.dateScopeRange[1] ? toStringMaximumTimeOfDay(dataMap.searchFormModel.dateScopeRange[1]) : null
        }
        let dueDateScopeFrom = null as string | null
        let dueDateScopeTo = null as string | null
        if (dataMap.searchFormModel.dueDateScopeRange && dataMap.searchFormModel.dueDateScopeRange.length > 1) {
          dueDateScopeFrom = dataMap.searchFormModel.dueDateScopeRange[0] ? formatDate(dataMap.searchFormModel.dueDateScopeRange[0]) : null
          dueDateScopeTo = dataMap.searchFormModel.dueDateScopeRange[1] ? toStringMaximumTimeOfDay(dataMap.searchFormModel.dueDateScopeRange[1]) : null
        }

        const params: AcceptanceReceiptPageBOCParamModel = {
          bankCode: props.bankCode,
          draftNo: dataMap.searchFormModel.draftNo,
          dateScopeFrom: dateScopeFrom,
          dateScopeTo: dateScopeTo,
          dueDateScopeFrom: dueDateScopeFrom,
          dueDateScopeTo: dueDateScopeTo,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getBOCReceiptPageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
          if (data?.code !== 200 && data?.msg) {
            ElMessage({
              message: data.msg,
              type: 'error',
              duration: 2000
            })
          }
        }
        dataMap.total = data?.data?.totalCount ?? 0
        tableRef.value.clearSelection()
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      }
    })

    const approvalProcessItemClick = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.selectedRows = tableRef.value.getSelectionRows()
        dataMap.receiveType = 1
        dataMap.approvalProcessDialogVisible = true
      } else {
        ElMessage({
          message: t('Acceptance.selectApprovalTip'),
          type: 'error'
        })
      }
    }
    const rejectApprovalItemClick = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.selectedRows = tableRef.value.getSelectionRows()
        dataMap.receiveType = 2
        dataMap.approvalProcessDialogVisible = true
      } else {
        ElMessage({
          message: t('Acceptance.selectApprovalTip'),
          type: 'error'
        })
      }
    }
    const handleBatchAcceptance = () => {
      dataMap.receiveType = 1
      dataMap.approvedDialogVisible = true
    }
    const handleBatchReject = () => {
      dataMap.receiveType = 2
      dataMap.approvedDialogVisible = true
    }
    const viewDetail = async(row: ReceiveAcceptanceHistoryModel) => {
      dataMap.approvalData = row
      dataMap.detailDialogVisible = true
    }
    const onApprovalProcessDialogConfirm = async() => {
      await dataMap.getPageList(dataMap.page, dataMap.limit)
    }
    onMounted(() => {
      if (props.isActive) {
        dataMap.getPageList(null, 10)
      }
    })
    watch(() => props.isActive, (isActive) => {
      if (isActive) {
        dataMap.getPageList(null, 10)
      } else {
        dataMap.tableData = []
        tableRef.value.clearSelection()
      }
    })
    return {
      t,
      tableRef,
      approvalProcessItemClick,
      rejectApprovalItemClick,
      handleBatchAcceptance,
      handleBatchReject,
      viewDetail,
      onApprovalProcessDialogConfirm,
      ...toRefs(dataMap)
    }
  }
})
